<template>
  <div class="flsa-survey-summary-cards">
    <WorkflowSummaryCardsSkeleton
      v-if="isLoading"
      class="summary-cards-skeleton"
    />
    <template v-else>
      <WorkflowUpToDateCard
        card-title="OK"
        :icon="FLSA_ALERT_ICONS.up_to_date"
        :percentage="upToDatePct"
        :label="upToDateLabel"
        :selected="isCardSelected(['up_to_date'])"
        :compact="compactCards"
        @click="handleCardClick(['up_to_date'])"
      />
      <WorkflowSummaryCard
        v-for="card in summaryCards"
        :key="card.key"
        caption="job profiles"
        :icon="card.icon"
        :icon-class="card.iconClass"
        :border-class="card.borderClass"
        :label="card.label"
        :value="card.value"
        :tooltip="card.tooltip"
        :selected="isCardSelected(card.optionIds)"
        :compact="compactCards"
        @click="handleCardClick(card.optionIds, card.label)"
      />
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { getTooltip } from '@/helpers/tooltips';
import { useFlsaSurveysStore } from '@/stores/jobProfiles/flsaSurveys';
import { useAppBase } from '@/stores/app/base';
import { useFilterDrawer } from '@/stores/filters/filterDrawer';
import { useRouter, useRoute } from 'vue-router';
import WorkflowSummaryCardsSkeleton from '@/components/library/shared/WorkflowSummaryCardsSkeleton.vue';
import WorkflowUpToDateCard from '@/components/library/shared/WorkflowUpToDateCard.vue';
import WorkflowSummaryCard from '@/components/library/shared/WorkflowSummaryCard.vue';
import { FLSA_ALERT_LABELS, FLSA_ALERT_ICONS } from '@/components/library/flsaSurveys/helpers';
import { useTracking } from '@/stores/tracking/tracking';

const COMPACT_WHEN_FILTER_OPEN_MAX_WIDTH = 1400;

const props = defineProps({
  totals: { type: Object, default: () => ({}) },
  isLoading: { type: Boolean, default: false },
});

const { selectAlertStatus, updateSelectedAlertStatuses } = useFlsaSurveysStore();
const { selectedAlertStatuses } = storeToRefs(useFlsaSurveysStore());
const { windowWidth } = storeToRefs(useAppBase());
const { open: filterDrawerOpen } = storeToRefs(useFilterDrawer());
const router = useRouter();
const route = useRoute();

const upToDateCount = computed(() => props.totals?.up_to_date_profiles || 0);
const profilesWithAlerts = computed(() => props.totals?.profiles_with_alerts || 0);
const totalInScope = computed(() => upToDateCount.value + profilesWithAlerts.value);

const upToDatePct = computed(() => {
  if (!totalInScope.value) return 0;
  return Math.round((upToDateCount.value / totalInScope.value) * 100);
});

const upToDateLabel = computed(() => {
  if (!totalInScope.value) return '0%';

  const rounded = Math.round((upToDateCount.value / totalInScope.value) * 100);

  if (upToDateCount.value > 0 && rounded === 0) return '<1%';

  return `${rounded}%`;
});

const compactCards = computed(() => (
  filterDrawerOpen.value && windowWidth.value < COMPACT_WHEN_FILTER_OPEN_MAX_WIDTH
));

const handleCardClick = (optionIds, label = '') => {
  const statusLabel = label || optionIds.map((id) => FLSA_ALERT_LABELS[id] || id).join(', ');

  useTracking().flsaLibSelectedMetricCard({ status: statusLabel });

  selectAlertStatus(optionIds, router);
};

const isCardSelected = (optionIds) => {
  const selected = [...selectedAlertStatuses.value].sort();
  const sorted = [...optionIds].sort();

  return selected.length === sorted.length
    && selected.every((id, i) => id === sorted[i]);
};

const summaryCards = computed(() => {
  const { totals = {} } = props;

  return [
    {
      key: 'no_flsa_submission',
      label: FLSA_ALERT_LABELS.no_flsa_submission,
      value: totals.no_flsa_submission || 0,
      borderClass: 'border-no-survey',
      iconClass: 'icon-no-survey',
      icon: FLSA_ALERT_ICONS.no_flsa_submission,
      optionIds: ['no_flsa_submission'],
      tooltip: getTooltip('flsa-report-alert-no-survey'),
    },
    {
      key: 'outdated_form',
      label: FLSA_ALERT_LABELS.outdated_form,
      value: totals.outdated_form || 0,
      borderClass: 'border-out-of-date-survey',
      iconClass: 'icon-out-of-date-survey',
      icon: FLSA_ALERT_ICONS.outdated_form,
      optionIds: ['outdated_form'],
      tooltip: getTooltip('flsa-report-alert-out-of-date'),
    },
    {
      key: 'status_mismatch',
      label: FLSA_ALERT_LABELS.status_mismatch,
      value: totals.status_mismatch || 0,
      borderClass: 'border-mismatched',
      iconClass: 'icon-mismatched',
      icon: FLSA_ALERT_ICONS.status_mismatch,
      optionIds: ['status_mismatch'],
      tooltip: getTooltip('flsa-report-alert-mismatched'),
    },
    {
      key: 'review_overdue',
      label: 'Review Overdue',
      value: totals.review_overdue || 0,
      borderClass: 'border-out-of-sla',
      iconClass: 'icon-out-of-sla',
      icon: FLSA_ALERT_ICONS.review_overdue,
      optionIds: ['review_overdue'],
      tooltip: getTooltip('flsa-report-alert-out-of-sla'),
    },
  ];
});

onMounted(() => {
  if (route.query?.alert_type) {
    if (Array.isArray(route.query.alert_type)) {
      updateSelectedAlertStatuses(route.query.alert_type);
    } else {
      updateSelectedAlertStatuses(route.query.alert_type.split(','));
    }
  }
});
</script>

<style lang="less" scoped>
.flsa-survey-summary-cards {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 24px;
  margin-bottom: 24px;
  min-width: 0;
}

.summary-cards-skeleton {
  grid-column: 1 / -1;
  min-width: 0;
}
</style>
